/* 0.42.62 - The flow runtime.
 *
 * Mounts once for the whole flow and stays mounted while the citizen
 * moves between steps, which is what keeps their answers alive: nothing
 * about a run is stored anywhere, by decision, so the browser's memory
 * is the only place they exist.
 *
 * A reload therefore loses everything. That is honest rather than
 * accidental, and the citizen is warned before it happens.
 */

import { goTo } from '../components/SpaLinks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import PageContentRenderer, { resolveAdminText, FullBleedRule, titleSpacingFor } from '../components/PageContentRenderer';
import HenaketStepper from '../components/HenaketStepper';
import { isLoginRequired, goToLogin } from '../lib/requireLogin';
import BackLink from '../components/BackLink';
import { resolveTitleText } from '../lib/resolveTitleText';
import { buildExpressionFunctions } from '../lib/rules/evaluate';
import { FlowContext } from '../context/FlowContext';
import { useLanguage } from '../context/LanguageContext';
import { getFlow, getFlowStep, getFlowError } from '../api/pages';
import { loadVisitApi } from '../lib/visitApi';
import useHashScroll from '../lib/useHashScroll';
import { readUrlValues, missingRequired } from '../lib/urlValues';
import { loadTargets, takeHandover, missingTarget } from '../lib/valueHandover';

/* 0.46.01 - Every step keeps its values under its STEP ADDRESS: the
 * instance name when it has one, else the form's own address. So a
 * later step reads an earlier one as `wiposixter2searchresults.mark`
 * without an instance name having to be invented for a form used once.
 *
 * A step sees:
 *   - every stored value, under `address.field`;
 *   - the plain names of the steps WITHOUT an instance name, as before
 *     (an instance name keeps a form used twice apart, so those stay
 *     prefixed only), the later step winning a clash;
 *   - its own fields under their plain names, over everything else.
 * So a page's own rules work unchanged whether it is used alone or in
 * a flow, and rules written before this ship keep reading plain names.
 */
function stepAddress(st) {
  return (st && (st.segment || st.instance_key || st.page_slug)) || '';
}

function viewForStep(allValues, steps, index) {
  const view = {};
  const own = stepAddress(steps[index]);
  steps.forEach((st, i) => {
    if (i === index || !st || st.instance_key) return;
    const prefix = `${stepAddress(st)}.`;
    if (prefix === '.') return;
    for (const [k, v] of Object.entries(allValues)) {
      if (k.startsWith(prefix)) view[k.slice(prefix.length)] = v;
    }
  });
  Object.assign(view, allValues);
  if (own) {
    const prefix = `${own}.`;
    for (const [k, v] of Object.entries(allValues)) {
      if (k.startsWith(prefix)) view[k.slice(prefix.length)] = v;
    }
  }
  return view;
}

/* Writing back: a name that already carries a dot (another step's
 * value, a card's `card.title`) is left alone, anything else belongs to
 * this step's address. */
function keyForWrite(name, address, flowNames) {
  if (!address || name.includes('.')) return name;
  // 0.48.90 - The flow's server copies (and their _state, _failure)
  // belong to the flow, not to the step that changed them.
  if (flowNames && flowNames.has(name.replace(/_(state|failure|refused|dirty)$/, ''))) return name;
  return `${address}.${name}`;
}

export default function FlowRunner() {
  const { slug, step: stepParam } = useParams();
  const { language, t } = useLanguage();
  // 0.49.20 - A platform text, or its English default.
  const tx = (k, d) => { const v = t ? t(k) : ''; return v && v !== k ? v : d; };
  const navigate = useNavigate();
  const location = useLocation();

  const [flow, setFlow] = useState(null);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState({});     // page slug -> page
  const [values, setValues] = useState({});
  // 0.48.86 - Starting with the answers kept for the visit.
  const [apiState, setApiState] = useState(() => loadVisitApi());
  /* 0.46.00 - What a step looked like when the citizen left it: each
     paginator's page, and how far down the page they were. Going back
     shows the step as it was (page 7 of the results, at the card they
     clicked) rather than from the top. Kept by step index, cleared with
     the answers on "start the flow again", gone on a reload. */
  const pagingByStep = useRef({});
  const scrollByStep = useRef({});
  const pendingScroll = useRef(null);
  /* 0.43.89 - Flow errors. The flow is opened directly on a step past the
   * first (a reload, or a link to a later step): the answers are not kept
   * yet, so they are gone - error "page_reloaded". With an error page set
   * for it, that page is shown; without one, the flow starts at step 1.
   * Moving between steps inside the flow never triggers it. */
  const [errorPage, setErrorPage] = useState(null);
  const enteredRef = useRef(null);       // the step index the flow was opened on
  const errorAtRef = useRef(null);       // the address the error page was shown at
  const [errorPending, setErrorPending] = useState(false);
  // 0.49.19 - #element_id in a step's address scrolls to that element.
  useHashScroll(!!flow && Object.keys(pages).length > 0);

  /* 0.42.81 - The step is addressed by NAME: /services/vehicle/goods.
   *
   * A number said nothing and broke whenever the flow was reordered. A
   * name says what the step asks and follows the step if it moves.
   *
   * A number still works, because links were shared while it was the
   * only form of address; it is swapped for the name below. */
  const steps = useMemo(() => (flow && Array.isArray(flow.steps) ? flow.steps : []), [flow]);
  const segmentOf = (st, i) => (st && (st.segment || st.instance_key || st.page_slug)) || String(i + 1);
  const byName = steps.findIndex((st, i) => segmentOf(st, i) === stepParam);
  const asNumber = parseInt(stepParam, 10);
  const index = byName >= 0
    ? byName
    : Math.min(Math.max(0, (Number.isFinite(asNumber) ? asNumber : 1) - 1),
               Math.max(0, steps.length - 1));

  /* 0.46.00 - Back to a step: return to where it was left, once its
     content is on the page (twice, as cards and pictures settle). */
  useEffect(() => {
    const y = pendingScroll.current;
    if (y == null) return undefined;
    pendingScroll.current = null;
    const go = () => window.scrollTo({ top: y });
    const t1 = setTimeout(go, 0);
    const t2 = setTimeout(go, 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [index]);


  /* 0.42.83 - A flow lives under /services/, and only there. */
  const wrongPrefix = (() => {
    if (!flow) return false;
    return location.pathname.split('/')[2] === 'p';
  })();

  useEffect(() => {
    let alive = true;
    getFlow(slug)
      .then(d => { if (alive) setFlow(d); })
      .catch((e) => {
        // 0.46.99 - Requires login: to the login page and back.
        if (alive && isLoginRequired(e)) { goToLogin(navigate, location); return; }
        if (alive) setError('not-found');
      });
    return () => { alive = false; };
  }, [slug]);

  const current = steps[index] || null;

  useEffect(() => {
    if (!flow || !steps.length || enteredRef.current !== null) return;
    enteredRef.current = index;
    if (index <= 0) return;
    const hasPage = (flow.error_pages || []).some(e => e.error_id === 'page_reloaded');
    if (!hasPage) {
      navigate(`/${language}/services/${slug}/${segmentOf(steps[0], 0)}`, { replace: true });
      return;
    }
    errorAtRef.current = stepParam;
    setErrorPending(true);
    getFlowError(slug, 'page_reloaded', language)
      .then(d => { setErrorPage(d); setErrorPending(false); })
      .catch(() => {
        setErrorPending(false);
        navigate(`/${language}/services/${slug}/${segmentOf(steps[0], 0)}`, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, steps.length]);

  /* 0.43.98 - "Start the flow again": every answer and API result is
     cleared, the error page (if shown) gives way, and the flow opens on
     step 1. */
  const restartFlow = () => {
    setValues({});
    setApiState(loadVisitApi());
    pagingByStep.current = {};
    scrollByStep.current = {};
    setErrorPage(null);
    errorAtRef.current = null;
    if (steps.length) goTo(0);
  };

  /* 0.43.90 - A rule set the flow's error state. The page set for that
     Error ID is shown; with none set for it, the citizen stays on the
     current step (a missing page never strands them on a blank screen). */
  const failFlow = (errorId) => {
    const id = String(errorId || '').trim();
    if (!id || !flow) return;
    if (!(flow.error_pages || []).some(e => e.error_id === id)) return;
    errorAtRef.current = stepParam;
    setErrorPending(true);
    getFlowError(slug, id, language)
      .then(d => { setErrorPage(d); setErrorPending(false); })
      .catch(() => { setErrorPending(false); errorAtRef.current = null; });
  };

  // A link on the error page (e.g. back to the start) moves the address:
  // the error page gives way to the flow again.
  useEffect(() => {
    if (errorPage && errorAtRef.current !== null && stepParam !== errorAtRef.current) {
      setErrorPage(null);
      errorAtRef.current = null;
    }
  }, [stepParam, errorPage]);

  /* 0.42.92 - The name to show: the citizen's language if the flow has
     one, otherwise the flow's own name. */
  /* 0.42.95 - The STEP's own return control, from that form's settings.
   *
   * A form in a flow is rendered by the flow, not by the page component,
   * so its return button was never drawn at all. The same rules as a
   * page's: off unless the form asks for it, a per-language label that
   * may itself be a library value, and either the browser's history or
   * a URL of the form's choosing. */
  const returnControl = (() => {
    const page = current && pages[current.page_slug];
    const rb = page && page.settings && page.settings.return_button;
    if (!rb || !rb.enabled) return null;
    const pick = () => {
      const l = rb.label;
      if (typeof l === 'string') return l.trim();
      if (l && typeof l === 'object') {
        if (l[language]) return l[language];
        const first = Object.values(l).find(v => typeof v === 'string' && v.trim());
        return (first || '').trim();
      }
      return '';
    };
    const label = resolveTitleText(pick(), language) || 'Return';
    if (rb.mode === 'url') {
      const url = (rb.url || '').trim();
      if (!url) return null;
      if (/^https?:\/\//i.test(url)) {
        return <BackLink onClick={() => { goTo(url); }}>{label}</BackLink>;
      }
      return <BackLink to={url}>{label}</BackLink>;
    }
    return <BackLink onClick={() => window.history.back()}>{label}</BackLink>;
  })();


  /* 0.43.21 - A step's word in the citizen's language, falling back to
     the authoring text the older field holds. */
  const stepWord = (st, mapKey, legacyKey) => {
    const map = st && st[mapKey] && typeof st[mapKey] === 'object' ? st[mapKey] : {};
    return (map[language] || '').trim() || st[legacyKey] || '';
  };

  /* 0.42.93 - For resolving a library value in the citizen's language.
     A flow has no form values of its own, so the context carries the
     language and the functions only. */
  const textCtx = {
    values: {},
    language,
    expressionFunctions: buildExpressionFunctions({ lang: language, values: {}, structure: [] }),
  };

  /* 0.43.29 - The navigator's label. Declared AFTER textCtx, which it
     reads: it is an immediately-invoked function, so it ran where it
     stood and read textCtx before that line had executed, with {n} and {m} kept whole.
   *
   * It went through resolveAdminText, which resolves {token} against
   * the form's values - so {n} and {m} were read as fields, found
   * nothing, and resolved to nothing. "Քայլ {n}/{m}" arrived at the
   * chip as "Քայլ /".
   *
   * They are hidden behind two characters no text contains while the
   * library value is resolved, then put back, so a label can be a
   * library value AND still count its steps. The stepper does the
   * counting itself. */
  const stepperLabel = (() => {
    const raw = (flow && (flow.stepper_label || {})[language])
      || (flow && flow.stepper_label_authoring) || '';
    if (!raw.trim()) return 'Step {n} of {m}';
    const hidden = raw.replace(/\{n\}/g, '\uE000').replace(/\{m\}/g, '\uE001');
    return resolveAdminText(hidden, textCtx)
      .replace(/\uE000/g, '{n}')
      .replace(/\uE001/g, '{m}');
  })();

  const flowName = flow
    ? (((flow.names || {})[language] || '').trim() || flow.name || '')
    : '';

  /* 0.42.81 - An old numbered link becomes the named one, so a link
     shared before this keeps working and the citizen ends up at the
     address the flow now uses. */
  useEffect(() => {
    if (!flow || !current) return;
    const want = segmentOf(current, index);
    if (stepParam !== want) {
      navigate(`/${language}/services/${slug}/${want}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, current, index, stepParam]);

  // The step's page, fetched when first reached and kept after that, so
  // stepping back does not re-fetch.
  useEffect(() => {
    if (!current || pages[current.page_slug]) return;
    let alive = true;
    // 0.42.82 - Through the flow: a form does not answer on its own.
    getFlowStep(slug, segmentOf(current, index), language)
      .then(d => { if (alive) setPages(prev => ({ ...prev, [current.page_slug]: d })); })
      .catch((e) => {
        if (alive && isLoginRequired(e)) { goToLogin(navigate, location); return; }
        if (alive) setError('step-missing');
      });
    return () => { alive = false; };
  }, [current, pages, language]);

  /* A reload empties everything, so say so first. Only once there is
     something to lose. */
  const hasAnswers = Object.keys(values).length > 0;
  useEffect(() => {
    if (!hasAnswers) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasAnswers]);

  // 0.46.01 - The step's address (instance name, else form address).
  const instanceKey = stepAddress(current);
  /* 0.49.20 - The address's values the flow was opened with
     (?training=T-12), kept for the whole flow: moving between steps
     drops them from the address. A new address with values replaces them. */
  /* 0.49.21 - The address as it was (its query), read with the flow's
     Allowed values once the flow has arrived. */
  const [urlSearch, setUrlSearch] = useState(() => location.search);
  useEffect(() => {
    if (location.search && location.search !== urlSearch) setUrlSearch(location.search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  /* 0.49.54 - Values handed over through the server
     (lib/valueHandover.js), taken once when the flow opens and kept for
     all its steps. The address's own values are not read then. */
  const [valuesOff, setValuesOff] = useState(null);
  // 0.49.59 - The site's page for Link incomplete.
  const [sitePage, setSitePage] = useState('');
  useEffect(() => { let on = true; loadTargets().then(t => { if (on) { setValuesOff(!!t.off); setSitePage(t.linkIncompletePage || ''); } }); return () => { on = false; }; }, []);
  const serverValues = !!flow && (flow.values_source === 'server' || valuesOff === true);
  const entryRef = useRef({ key: location.key, marked: !!(location.state && location.state.handover) });
  const [hand, setHand] = useState(null);
  useEffect(() => {
    if (!serverValues) return undefined;
    let on = true;
    takeHandover('flow', slug, entryRef.current.key, entryRef.current.marked)
      .then(v => { if (on) setHand(v || {}); });
    return () => { on = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValues]);
  const valuesPending = !!flow && ((flow.url_values || []).length > 0)
    && (valuesOff === null || (serverValues && hand === null));
  const urlVals = useMemo(() => (serverValues ? (hand || {}) : readUrlValues(urlSearch, flow && flow.url_values).values),
    [urlSearch, flow, serverValues, hand]);
  const stepValues = useMemo(() => ({ ...viewForStep(values, steps, index), url: urlVals }),
    [values, steps, index, urlVals]);
  const urlMissing = flow && !valuesPending ? missingRequired(flow.url_values, urlVals) : [];
  // 0.49.54 - The page the admin chose for a visitor without the values.
  /* 0.49.59 - The flow's own choice, then its own Link incomplete
     error page (shown in place, below), then the site's page. */
  const ownErrorPage = !!(flow && (flow.error_pages || []).some(e => e.error_id === 'link_incomplete'));
  const missingTo = urlMissing.length > 0
    ? missingTarget((flow && flow.values_missing) || (ownErrorPage ? '' : sitePage), language) : null;
  useEffect(() => {
    if (missingTo) navigate(missingTo, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingTo]);
  /* 0.49.20 - A required address value missing: the flow's "Link
     incomplete" page, when it has one. */
  useEffect(() => {
    if (!flow || urlMissing.length === 0 || missingTo || errorPage || errorPending) return;
    if (!(flow.error_pages || []).some(e => e.error_id === 'link_incomplete')) return;
    errorAtRef.current = stepParam;
    setErrorPending(true);
    getFlowError(slug, 'link_incomplete', language)
      .then(d => { setErrorPage(d); setErrorPending(false); })
      .catch(() => { setErrorPending(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, urlMissing.length]);


  // 0.48.90 - The flow's server copies, by name.
  const flowCopyNames = useMemo(() => new Set(((flow && flow.server_copies) || [])
    .map(c => c && c.name).filter(Boolean)), [flow]);
  const onValueChange = useCallback((updater) => {
    setValues(prev => {
      const view = viewForStep(prev, steps, index);
      const next = typeof updater === 'function' ? updater(view) : updater;
      // 0.49.11 - Nothing changed: keep the same state, so no render follows.
      if (next === view) return prev;
      /* Only what CHANGED is written back.
       *
       * The step sees other steps' values under their plain names too,
       * so merging the whole view would copy every one of them into this
       * step's namespace on any keystroke - an earlier step's full_name
       * becoming home.full_name, and shadowing it from then on. */
      const merged = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(next)) {
        if (Object.is(view[k], v)) continue;
        merged[keyForWrite(k, instanceKey, flowCopyNames)] = v;
        changed = true;
      }
      // A field cleared entirely: the view loses the key, so the stored
      // one must go too, or a rule reading it sees a stale answer.
      for (const k of Object.keys(view)) {
        if (!(k in next)) { delete merged[keyForWrite(k, instanceKey, flowCopyNames)]; changed = true; }
      }
      return changed ? merged : prev;
    });
  }, [instanceKey, steps, index, flowCopyNames]);

  const goTo = (i) => {
    // 0.46.00 - Remember where this step was left; going BACK to a step
    // returns to where it was left, going forward starts at the top.
    scrollByStep.current[index] = window.scrollY;
    const back = i < index && scrollByStep.current[i] != null;
    pendingScroll.current = back ? scrollByStep.current[i] : null;
    // 0.42.63 - The flow's own prefix, the same choice a page makes.
    // 0.42.81 - and the step's name rather than its number.
    navigate(`/${language}/services/${slug}/${segmentOf(steps[i], i)}`);
    if (!back) window.scrollTo({ top: 0 });
  };

  if (error || wrongPrefix) {
    return <main className="container"><p>This service is not available.</p></main>;
  }
  /* 0.49.20 - A required address value is missing or not valid, and the
     flow has no "Link incomplete" page: a short message, no steps. */
  // 0.49.54 - Waiting for the handed values, or leaving for the page
  // chosen for a visitor without them.
  if (valuesPending || missingTo) return <main className="container" aria-busy="true" />;
  if (urlMissing.length > 0 && !errorPage && !errorPending
      && !((flow && flow.error_pages) || []).some(e => e.error_id === 'link_incomplete')) {
    return (
      <main className="container">
        <h1>{tx('common.link_incomplete', 'This link is incomplete')}</h1>
        <p>{tx('common.link_incomplete_text', 'Open this page from the link where you found it.')}</p>
      </main>
    );
  }
  /* 0.43.89 - The flow's error page, in place of the step. Its flow
     position is "before step 1", so a rule that moves the flow forward
     (a "Start again" button) lands on step 1. */
  if (errorPending && !errorPage) {
    return <main className="container" aria-busy="true" />;
  }
  if (errorPage) {
    const errorContext = {
      steps: steps.map((st, i) => ({
        key: `${st.page_slug}:${st.instance_key || ''}:${i}`,
        title: st.title || `Step ${i + 1}`, description: '',
        complete: false, locked: i > 0,
      })),
      currentIndex: -1,
      goTo: (i) => goTo(i),
      move: (i) => goTo(i),
      restart: restartFlow,
    };
    return (
      <FlowContext.Provider value={errorContext}>
        <main className="container">
          <PageContentRenderer
            structure={errorPage.structure || []}
            rules={errorPage.rules || []}
            settings={errorPage.settings || null}
          />
        </main>
      </FlowContext.Provider>
    );
  }
  if (!flow || !current) {
    return <main className="container" aria-busy="true" />;
  }
  const page = pages[current.page_slug];
  if (!page) {
    return <main className="container" aria-busy="true" />;
  }

  const last = index >= steps.length - 1;

  const flowContext = {
    steps: steps.map((st, i) => ({
      key: `${st.page_slug}:${st.instance_key || ''}:${i}`,
      title: st.title || (pages[st.page_slug] && pages[st.page_slug].title) || `Step ${i + 1}`,
      description: st.description || '',
      // Been past it, so it can be returned to; ahead of it, locked,
      // because a later step may rest on answers not given yet.
      complete: i < index,
      locked: i > index,
    })),
    currentIndex: index,
    // The Step navigator only goes back; a RULE may go either way,
    // because the form decides when forward is allowed.
    goTo: (i) => { if (i <= index) goTo(i); },
    move: (i) => goTo(i),
    // 0.43.90 - "set flow error" from a rule.
    fail: failFlow,
    restart: restartFlow,
  };

  return (
    <FlowContext.Provider value={flowContext}>
    <main className="container">
      {/* 0.42.92 - The flow's name, when the flow asks for it: above
          everything the step renders, which is above the return link,
          with a rule under it in the sub-header's own colour so the two
          lines read as the same system. Shown in the citizen's language
          when the flow has a name in it, else the flow's own. */}
      {flow.show_name && (flowName || '').trim() && (() => {
        /* 0.42.98 - The Title element's own heading and spacing rules.
         *
         * The tag carries Henaket's H1..H6 styling, and the margins come
         * from titleSpacingFor - the same function a title on a page
         * uses - so "symmetric" gives equal space above and below and
         * the title sits centred between the sub-header's line and the
         * rule underneath. One set of rules, not a second. */
        const level = Number((flow.name_level || 'h1').replace('h', '')) || 1;
        const sp = titleSpacingFor({ level, ...(flow.name_spacing || {}) });
        const Tag = `h${Math.min(Math.max(level, 1), 6)}`;
        return (
          <>
            <Tag style={{
              /* 0.43.00 - Minus the header's own bottom margin.
               *
               * The header adds space-6 (24px) below its hairline so no
               * page needs its own, and the title's margin landed on top
               * of it: 32 + 24 = 56 above, against 32 below, so "equal"
               * was not equal. Subtracting it makes the setting mean
               * what it says - the distance from the line to the text.
               *
               * max() so a small setting cannot pull the title up into
               * the header. */
              /* 0.45.66 - The header lost that margin in 0.45.43, so
                 the subtraction left almost no space under it. The
                 setting is now the whole distance from the line. */
              marginTop: `var(--space-${sp.top})`,
              marginBottom: `var(--space-${sp.bottom})`,
              color: 'var(--color-text-800)',
            }}>{flowName}</Tag>
            <FullBleedRule style={{
              height: '1px',
              background: 'var(--color-text-500)',
              border: 'none',
              margin: 0,
            }} />
          </>
        );
      })()}
      {/* 0.42.66 - What the Step element needs, and only a flow knows:
          the steps, where the citizen is, and how to move. A page that
          is not in a flow gets nothing here, and the element falls back
          to the steps its admin typed. */}
      {/* 0.42.95 - The step's return control, under the flow's name -
          or the first thing on the page when the name is not shown.
          0.43.00 - 12px below the rule; nothing extra when there is no
          rule above it, where the header's own margin already sits. */}
      {returnControl && (
        /* 0.45.66 - 24px under the sub-header when it is the first
           thing on the step, as on a page (StaticPage .returnRow): the
           header no longer brings that space itself. */
        <div style={{ marginTop: (flow.show_name && (flowName || '').trim())
          ? 'var(--space-3)' : 'var(--space-6)' }}>
          {returnControl}
        </div>
      )}

      {/* 0.42.93 - The navigator, when the flow asks for it. The flow
          shows it, not each form: only the flow knows how many steps
          there are and which one this is. A step's own title, or the
          form's name when the admin left it empty; both may be library
          values, resolved here for the citizen's language. */}
      {flow.show_stepper && (
        /* 0.46.37 - 24px below the navigator to the step's first
           element (was 16px, plus a title's own top space). */
        /* 0.46.93 / 0.46.95 - 16px under the navigation menu, as a
           page's first element. */
        <div data-stepper-col="" style={{ margin: 'var(--space-4) 0 var(--space-6)' }}>
          <HenaketStepper
            steps={steps.map((st, i) => ({
              key: `${st.page_slug}:${i}`,
              /* 0.43.21 - The citizen's language first, then the
                 authoring text in `title`, then the form's own name. A
                 step marked "authoring language only" has no entry for
                 this language, so it lands on the authoring text by the
                 same path - no separate rule for it. */
              title: resolveAdminText(stepWord(st, 'titles', 'title'), textCtx)
                || (pages[st.page_slug] && pages[st.page_slug].title)
                || st.page_slug,
              description: resolveAdminText(stepWord(st, 'descriptions', 'description'), textCtx),
              complete: i < index,
              locked: i > index,
            }))}
            currentIndex={index}
            onGoTo={(i) => { if (i <= index) goTo(i); }}
            label={stepperLabel}
          />
        </div>
      )}
      {/* 0.45.80 - A fresh renderer for every step visit. The same
          component was reused from step to step, so its "the page
          opens" moment happened only once per flow: coming back to a
          step (after Back, or after changing an answer on an earlier
          step) never ran the step's page-open rules, and its call API
          did not repeat. The answers and API state live in the flow,
          so nothing the citizen gave is lost. */}
      <PageContentRenderer
        key={`${index}:${current.page_slug}:${location.key || ""}`}
        structure={page.structure || []}
        rules={page.rules || []}
        settings={page.settings || null}
        externalValues={stepValues}
        onValueChange={onValueChange}
        externalApiState={apiState}
        onApiStateChange={setApiState}
        paginationState={pagingByStep.current[index] || null}
        onPaginationStateChange={(st) => { pagingByStep.current[index] = st; }}
        pageSlug={current.page_slug || ''}
        flowSlug={slug}
        flowCopies={[...flowCopyNames]}
      />
      {/* 0.42.85 - No Next or Back here. A form carries its own
          buttons and moves the flow from its own rules, so the runtime
          does not impose a pair the design never asked for. */}
    </main>
    </FlowContext.Provider>
  );
}
